/* eslint-disable @typescript-eslint/no-explicit-any */
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// NOTE: This file is a replacement for useThrottledCallback from
// https://github.com/antonioru/beautiful-react-hooks/blob/master/src/useThrottledCallback.ts
import { useCallback, useEffect, useRef } from 'react';
var defaultOptions = {
    leading: false,
    trailing: true,
};
// This is a throttle implementation meant to mirror to lodash.throttle so
// we dont have to add it as a dependency
function throttle(func, wait, options) {
    var _a, _b;
    if (options === void 0) { options = {}; }
    var timeout = null;
    var lastArgs = null;
    var lastCallTime = null;
    var result;
    var leading = (_a = options.leading) !== null && _a !== void 0 ? _a : false;
    var trailing = (_b = options.trailing) !== null && _b !== void 0 ? _b : true;
    var invoke = function (time, args) {
        lastCallTime = time;
        result = func.apply(void 0, __spreadArray([], __read(args), false));
    };
    var throttled = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var now = Date.now();
        if (lastCallTime === null && !leading) {
            lastCallTime = now;
        }
        var remaining = wait - (now - (lastCallTime !== null && lastCallTime !== void 0 ? lastCallTime : 0));
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            invoke(now, args);
        }
        else if (trailing) {
            lastArgs = args;
            if (!timeout) {
                timeout = setTimeout(function () {
                    timeout = null;
                    if (lastArgs) {
                        invoke(Date.now(), lastArgs);
                        lastArgs = null;
                    }
                }, remaining);
            }
        }
        return result;
    };
    throttled.cancel = function () {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
        lastArgs = null;
        lastCallTime = null;
    };
    return throttled;
}
// This was a dependency from useThrottledCallback source code
var useWillUnmount = function (callback) {
    useEffect(function () {
        return function () {
            callback();
        };
    }, [callback]);
};
/**
 * Accepts a function and returns a new throttled yet memoized version of that same function that waits the defined time
 * before allowing the next execution.
 * If time is not defined, its default value will be 600ms.
 */
export var useThrottledCallback = function (fn, dependencies, wait, options) {
    if (dependencies === void 0) { dependencies = []; }
    if (wait === void 0) { wait = 600; }
    if (options === void 0) { options = defaultOptions; }
    var throttled = useRef(throttle(fn, wait, options));
    useEffect(function () {
        throttled.current = throttle(fn, wait, options);
        // Disabling these warnings, since it is mirroring what the source does
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, __spreadArray([fn, wait, options], __read(dependencies), false));
    useWillUnmount(function () {
        var _a;
        (_a = throttled.current) === null || _a === void 0 ? void 0 : _a.cancel();
    });
    return useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return throttled.current.apply(throttled, __spreadArray([], __read(args), false));
    }, __spreadArray([], __read(dependencies), false));
};
